import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, 
  Bike, 
  MapPin, 
  Navigation, 
  Package, 
  DollarSign, 
  Star,
  Clock,
  CheckCircle,
  Phone,
  MessageSquare,
  Loader2,
  Power,
  ChevronRight,
  TrendingUp,
  Award,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${window.location.origin}/api`;

const VEHICLE_TYPES = [
  { id: 'car', label: 'Car', icon: Car },
  { id: 'motorcycle', label: 'Motorcycle', icon: Bike },
  { id: 'bicycle', label: 'Bicycle', icon: Bike },
  { id: 'walking', label: 'Walking', icon: Navigation }
];

const DELIVERY_STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  accepted: 'bg-blue-500/20 text-blue-400',
  picked_up: 'bg-purple-500/20 text-purple-400',
  in_transit: 'bg-orange-500/20 text-orange-400',
  delivered: 'bg-green-500/20 text-green-400'
};

export default function DeliveryDriverDashboard() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [driverLocation, setDriverLocation] = useState(null);
  
  // Registration dialog
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [vehicleType, setVehicleType] = useState('car');
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleDescription, setVehicleDescription] = useState('');
  
  // Location tracking
  const locationWatchId = useRef(null);
  const [isOnline, setIsOnline] = useState(false);
  const [togglingOnline, setTogglingOnline] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    return () => {
      if (locationWatchId.current) {
        navigator.geolocation.clearWatch(locationWatchId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      startLocationTracking();
    } else {
      if (locationWatchId.current) {
        navigator.geolocation.clearWatch(locationWatchId.current);
        locationWatchId.current = null;
      }
    }
  }, [isOnline]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/delivery/status`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        
        if (data.is_driver && data.driver?.is_online) {
          setIsOnline(true);
          fetchAvailableDeliveries();
        }
      }
    } catch (error) {
      console.error('Error fetching driver status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDeliveries = async () => {
    if (!driverLocation) {
      // Try to get location first
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            setDriverLocation({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            });
            await fetchDeliveriesWithLocation(pos.coords.latitude, pos.coords.longitude);
          },
          (err) => console.error('Location error:', err)
        );
      }
      return;
    }
    
    await fetchDeliveriesWithLocation(driverLocation.latitude, driverLocation.longitude);
  };

  const fetchDeliveriesWithLocation = async (lat, lng) => {
    try {
      const res = await fetch(
        `${API}/delivery/available?latitude=${lat}&longitude=${lng}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAvailableDeliveries(data.deliveries || []);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    }
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    locationWatchId.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const newLocation = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        };
        setDriverLocation(newLocation);
        
        // Update location on server
        try {
          await fetch(`${API}/delivery/location`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(newLocation)
          });
        } catch (error) {
          console.error('Error updating location:', error);
        }
      },
      (err) => console.error('Location tracking error:', err),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
    );
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const res = await fetch(`${API}/delivery/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          vehicle_type: vehicleType,
          license_plate: licensePlate || null,
          vehicle_description: vehicleDescription || null
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Registered as delivery driver!');
        setRegisterDialogOpen(false);
        fetchStatus();
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleToggleOnline = async () => {
    setTogglingOnline(true);
    
    // Get current location if going online
    let location = driverLocation;
    if (!isOnline && !location && navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        };
        setDriverLocation(location);
      } catch (error) {
        toast.error('Please enable location services');
        setTogglingOnline(false);
        return;
      }
    }

    try {
      const res = await fetch(`${API}/delivery/online`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_online: !isOnline,
          location
        })
      });

      if (res.ok) {
        setIsOnline(!isOnline);
        toast.success(isOnline ? 'You are now offline' : 'You are now online');
        
        if (!isOnline) {
          fetchAvailableDeliveries();
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setTogglingOnline(false);
    }
  };

  const handleAcceptDelivery = async (deliveryId) => {
    try {
      const res = await fetch(`${API}/delivery/accept/${deliveryId}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Delivery accepted!');
        fetchStatus();
        setAvailableDeliveries([]);
      } else {
        toast.error(data.detail || 'Failed to accept delivery');
      }
    } catch (error) {
      toast.error('Failed to accept delivery');
    }
  };

  const handleUpdateDeliveryStatus = async (deliveryId, newStatus) => {
    try {
      const res = await fetch(`${API}/delivery/${deliveryId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Status updated to: ${newStatus.replace('_', ' ')}`);
        fetchStatus();
        
        if (newStatus === 'delivered') {
          fetchAvailableDeliveries();
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 skeleton rounded-xl" />
        <div className="h-48 skeleton rounded-xl" />
      </div>
    );
  }

  // Not a driver yet - show registration
  if (!status?.is_driver) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <Car className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Become a Delivery Driver</h2>
          <p className="text-zinc-400 max-w-md mx-auto">
            Earn MEL by delivering orders from local businesses to customers.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="glass-card rounded-xl p-4 text-center">
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <h3 className="font-semibold text-white">Earn Money</h3>
            <p className="text-zinc-500 text-sm">Get paid for every delivery plus tips</p>
          </div>
          <div className="glass-card rounded-xl p-4 text-center">
            <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <h3 className="font-semibold text-white">Flexible Hours</h3>
            <p className="text-zinc-500 text-sm">Work whenever you want</p>
          </div>
          <div className="glass-card rounded-xl p-4 text-center">
            <MapPin className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <h3 className="font-semibold text-white">Local Deliveries</h3>
            <p className="text-zinc-500 text-sm">Stay in your neighborhood</p>
          </div>
        </div>

        {!status?.can_register ? (
          <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 mb-6">
            <div className="flex items-center gap-2 text-yellow-400">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{status?.register_reason || 'Not eligible to register'}</span>
            </div>
            <p className="text-zinc-400 text-sm mt-2">
              You need a Melanin Account with completed KYC verification to become a delivery driver.
            </p>
          </div>
        ) : (
          <button
            onClick={() => setRegisterDialogOpen(true)}
            className="w-full btn-primary py-3 text-lg"
            data-testid="register-driver-btn"
          >
            Start Delivering
          </button>
        )}

        {/* Registration Dialog */}
        <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
          <DialogContent className="bg-[#0F0F0F] border border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Register as Driver</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Tell us about your vehicle to get started
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div>
                <label className="text-zinc-400 text-sm mb-3 block">Vehicle Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {VEHICLE_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setVehicleType(type.id)}
                      className={`p-4 rounded-xl border flex items-center gap-3 transition-colors ${
                        vehicleType === type.id
                          ? 'bg-green-500/10 border-green-500/50'
                          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <type.icon className={`w-6 h-6 ${
                        vehicleType === type.id ? 'text-green-400' : 'text-zinc-400'
                      }`} />
                      <span className="text-white">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {(vehicleType === 'car' || vehicleType === 'motorcycle') && (
                <>
                  <div>
                    <label className="text-zinc-400 text-sm mb-2 block">License Plate</label>
                    <Input
                      value={licensePlate}
                      onChange={(e) => setLicensePlate(e.target.value)}
                      placeholder="ABC 1234"
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 text-sm mb-2 block">Vehicle Description</label>
                    <Input
                      value={vehicleDescription}
                      onChange={(e) => setVehicleDescription(e.target.value)}
                      placeholder="e.g., Blue Honda Civic"
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <button
                onClick={() => setRegisterDialogOpen(false)}
                className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRegister}
                disabled={registering}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500 disabled:opacity-50"
              >
                {registering ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Registering...
                  </>
                ) : (
                  'Register'
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    );
  }

  // Driver dashboard
  const driver = status.driver;
  const activeDelivery = status.active_delivery;
  const todayStats = status.today_stats;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Online Toggle & Stats Header */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              isOnline ? 'bg-green-500/20' : 'bg-zinc-800'
            }`}>
              {VEHICLE_TYPES.find(v => v.id === driver.vehicle_type)?.icon && (
                <Car className={`w-7 h-7 ${isOnline ? 'text-green-400' : 'text-zinc-400'}`} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Driver Mode</h2>
              <p className={`text-sm ${isOnline ? 'text-green-400' : 'text-zinc-500'}`}>
                {isOnline ? 'Online - Accepting deliveries' : 'Offline'}
              </p>
            </div>
          </div>

          <button
            onClick={handleToggleOnline}
            disabled={togglingOnline}
            className={`px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors ${
              isOnline 
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                : 'bg-green-500 text-white hover:bg-green-400'
            }`}
            data-testid="toggle-online-btn"
          >
            {togglingOnline ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Power className="w-5 h-5" />
            )}
            {isOnline ? 'Go Offline' : 'Go Online'}
          </button>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
            <p className="text-zinc-500 text-sm">Today's Deliveries</p>
            <p className="text-2xl font-bold text-white">{todayStats?.count || 0}</p>
          </div>
          <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
            <p className="text-zinc-500 text-sm">Earnings</p>
            <p className="text-2xl font-bold text-green-400">{(todayStats?.earnings || 0).toFixed(0)} MEL</p>
          </div>
          <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
            <p className="text-zinc-500 text-sm">Tips</p>
            <p className="text-2xl font-bold text-purple-400">{(todayStats?.tips || 0).toFixed(0)} MEL</p>
          </div>
        </div>
      </div>

      {/* Active Delivery */}
      {activeDelivery && (
        <div className="glass-card rounded-xl p-6 border-2 border-green-500/30">
          <div className="flex items-center gap-2 text-green-400 mb-4">
            <Package className="w-5 h-5" />
            <span className="font-semibold">Active Delivery</span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{activeDelivery.business?.name || 'Business'}</p>
                <p className="text-zinc-400 text-sm">{activeDelivery.pickup_location?.address}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${DELIVERY_STATUS_COLORS[activeDelivery.status]}`}>
                {activeDelivery.status.replace('_', ' ')}
              </span>
            </div>

            <div className="flex items-center gap-2 text-zinc-400">
              <Navigation className="w-4 h-4" />
              <span className="text-sm">Deliver to: {activeDelivery.dropoff_location?.address}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-xl">
              <span className="text-zinc-400">Earnings</span>
              <span className="text-green-400 font-semibold">{activeDelivery.driver_earnings} MEL</span>
            </div>

            {/* Status Actions */}
            <div className="flex gap-3">
              {activeDelivery.status === 'accepted' && (
                <button
                  onClick={() => handleUpdateDeliveryStatus(activeDelivery.delivery_id, 'picked_up')}
                  className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-500"
                >
                  Mark as Picked Up
                </button>
              )}
              {activeDelivery.status === 'picked_up' && (
                <button
                  onClick={() => handleUpdateDeliveryStatus(activeDelivery.delivery_id, 'in_transit')}
                  className="flex-1 py-3 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-500"
                >
                  Start Delivery
                </button>
              )}
              {activeDelivery.status === 'in_transit' && (
                <button
                  onClick={() => handleUpdateDeliveryStatus(activeDelivery.delivery_id, 'delivered')}
                  className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium hover:bg-green-500"
                  data-testid="complete-delivery-btn"
                >
                  Complete Delivery
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Deliveries */}
      {isOnline && !activeDelivery && (
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Available Deliveries</h3>
            <button
              onClick={fetchAvailableDeliveries}
              className="text-zinc-400 hover:text-white text-sm"
            >
              Refresh
            </button>
          </div>

          {availableDeliveries.length > 0 ? (
            <div className="space-y-3">
              {availableDeliveries.map(delivery => (
                <div
                  key={delivery.delivery_id}
                  className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <Package className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{delivery.business?.name || 'Business'}</p>
                        <p className="text-zinc-500 text-sm">{delivery.distance_to_pickup} mi away</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-semibold">{delivery.driver_earnings} MEL</p>
                      <p className="text-zinc-500 text-xs">{delivery.distance_miles} mi total</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleAcceptDelivery(delivery.delivery_id)}
                    className="w-full py-2 rounded-lg bg-green-500/20 text-green-400 font-medium hover:bg-green-500/30 transition-colors"
                  >
                    Accept Delivery
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No deliveries available nearby</p>
              <p className="text-zinc-500 text-sm">Check back soon!</p>
            </div>
          )}
        </div>
      )}

      {/* Overall Stats */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4">Your Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{driver.total_deliveries}</p>
            <p className="text-zinc-500 text-sm">Total Deliveries</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-400">{driver.total_earnings.toFixed(0)}</p>
            <p className="text-zinc-500 text-sm">Total Earnings (MEL)</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-400">{driver.total_tips.toFixed(0)}</p>
            <p className="text-zinc-500 text-sm">Total Tips (MEL)</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span className="text-3xl font-bold text-white">{driver.average_rating.toFixed(1)}</span>
            </div>
            <p className="text-zinc-500 text-sm">Rating ({driver.rating_count} reviews)</p>
          </div>
        </div>
      </div>

      {/* Recent Deliveries */}
      {status.recent_deliveries?.length > 0 && (
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-semibold text-white mb-4">Recent Deliveries</h3>
          <div className="space-y-3">
            {status.recent_deliveries.slice(0, 5).map(delivery => (
              <div
                key={delivery.delivery_id}
                className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/50"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    delivery.status === 'delivered' ? 'bg-green-400' : 'bg-yellow-400'
                  }`} />
                  <div>
                    <p className="text-white text-sm">{delivery.pickup_location?.address?.slice(0, 30)}...</p>
                    <p className="text-zinc-500 text-xs">
                      {new Date(delivery.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-green-400 text-sm">{delivery.driver_earnings} MEL</p>
                  {delivery.tip_amount > 0 && (
                    <p className="text-purple-400 text-xs">+{delivery.tip_amount} tip</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
